import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material"
import { useLazyGetCheckoutConfigQuery } from "src/features/checkout/checkoutApi"
import {
  useCreateCheckoutMutation,
  useLazyGetCheckoutQuery,
  useLazyGetCustomerQuery,
  useReplaceLineItemsMutation,
} from "src/features/checkout/storefrontApi"
import {
  setCheckout,
  setCheckoutConfig,
  setCheckoutProductVendors,
  setCustomer,
  setLastCheckoutId,
  setCart,
  setLastCartId,
  setLineItemsUpdated,
} from "src/features/checkout/checkoutSlice"
import FullPageLoader from "../components/FullPageLoader"
import Cart from "../components/custom-checkout/common/Cart"
import Breadcrumbs from "../components/custom-checkout/common/Breadcrumbs"
import FooterLinks from "../components/custom-checkout/common/FooterLinks"
import { useLazyGetCartJsQuery } from "src/features/checkout/cartJsApi"
import { isEqual, sortBy } from "lodash"
import OrderLimitsModal from "../components/custom-checkout/common/OrderLimitsModal"
import AutoApplyVolumeDiscountsModal from "../components/custom-checkout/common/AutoApplyVolumeDiscountsModal"
import { useCartCreateMutation, useCartLinesAddMutation, useCartLinesRemoveMutation, useCartLinesUpdateMutation, useLazyGetCartQuery } from "src/features/checkout/storefrontCartApi"

function CheckoutCart() {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const initialized = useRef(false)
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down("md"))
  const { lastCartId, customerAccessToken, lineItemsUpdated, checkout, cart } =
    useSelector((state) => state.checkout)

  const [
    getCheckoutConfig,
    { data: getCheckoutConfigData, isLoading: isCheckoutConfigLoading },
  ] = useLazyGetCheckoutConfigQuery()

  const [getCustomer, { isLoading: isGetCustomerLoading }] =
    useLazyGetCustomerQuery()

  const [
    getCart,
    { data: getCartData, isLoading: isGetCartLoading },
  ] = useLazyGetCartQuery()

  const [createCart, { isLoading: isCreateCartLoading }] =
    useCartCreateMutation()

  const [getCartJs, { data: cartJsData, isLoading: isCartJsLoading }] =
    useLazyGetCartJsQuery()

  const [
    cartLinesAdd,
    { data: cartLinesAddData, isLoading: isCartLinesAddLoading },
  ] = useCartLinesAddMutation()

  const [
    cartLinesRemove,
    { data: cartLinesRemoveData, isLoading: isCartLinesRemoveLoading },
  ] = useCartLinesRemoveMutation()

  function newCart() {
    createCart()
      .then(({ data }) => {
        console.log('createCart', data)
        if (data.cartCreate.cart.id) {
          dispatch(setLastCartId(data.cartCreate.cart.id))
          loadCart(data.cartCreate.cart.id)
        }
      })
      .catch((error) => {
        // Retry until success
        setTimeout(() => newCart(), 2000)
      })
  }

  function loadCart(cartId = null) {
    if (!cartId) {
      // Use cart id from localStorage if cartId is null.
      cartId = lastCartId
    }

    if (cartId) {
      // A cart id is available. Retrieve cart data.
      getCart(cartId)
        .then(({ data }) => {
          console.log('getCart', data);
          if (!data?.cart?.id || data?.cart?.completedAt) {
            // If cart is invalid or has already been completed, create a new cart.
            newCart()
          }
        })
        .catch((error) => {
          newCart()
        })
    } else {
      // Cart id is not available, create a new cart.
      newCart()
    }
  }

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true

      if (location.pathname === "/") {
        navigate("/options")
      }

      getCartJs()
      getCheckoutConfig()

      // Check if customer access token is available.
      if (customerAccessToken) {
        // Customer access token is available. Retrieve customer information and last incomplete checkout if available.
        getCustomer(customerAccessToken.accessToken)
          .then(({ data }) => {
            if (!data?.customer?.id) {
              loadCart()
              return
            }

            dispatch(setCustomer(data.customer))
            loadCart()

            // if (
            //   data?.customer?.lastIncompleteCheckout?.id &&
            //   !data?.customer?.lastIncompleteCheckout?.completedAt
            // ) {
            //   // Customer has an incomplete checkout, retrieve checkout data.
            //   loadCart(data.customer.lastIncompleteCheckout.id)
            // } else {
            //   // Customer has NO incomplete checkout, load checkout normally.
            //   loadCart()
            // }
          })
          .catch((error) => {
            loadCart()
          })
      } else {
        // NO customer access token, load checkout normally.
        loadCart()
      }
    }
  }, [])

  useEffect(() => {
    if (getCartData?.cart?.id) {
      dispatch(setCart(getCartData.cart))
    }
  }, [getCartData])

  useEffect(() => {
    if (getCheckoutConfigData) {
      dispatch(setCheckoutConfig(getCheckoutConfigData))
    }
  }, [getCheckoutConfigData])

  useEffect(() => {
    if (typeof cartJsData === "undefined" || !cart.id || lineItemsUpdated)
      return

    if (cartJsData.items.length > 0) {
      const lineItemsFromCartJs = cartJsData.items.map(
        ({ variant_id, quantity }) => ({
          merchandiseId: `gid://shopify/ProductVariant/${variant_id}`,
          quantity,
        }),
      )
      const lineItemsFromCheckout = cart.lines?.edges
        .filter((lineItem) => lineItem?.node?.merchandise?.product?.title !== 'Hawaii G.E.T')
        .map(
          (lineItem) => ({
            merchandiseId: lineItem?.node?.merchandise?.id,
            quantity: lineItem?.node?.quantity,
          }),
        )

      console.log('cartJsData|checkout', {lineItemsFromCartJs, lineItemsFromCheckout})

      // Check if the items in cart.js and checkout are not the same.
      if (
        !isEqual(
          sortBy(lineItemsFromCartJs, "merchandiseId"),
          sortBy(lineItemsFromCheckout, "merchandiseId"),
        )
      ) {
        // Replace checkout lineItems with cart.js items if they are not the same.
        if (cart.lines?.edges.length) {
          console.log("Replace")
          cartLinesRemove({
            cartId: cart.id,
            lineIds: cart.lines?.edges
              .filter((lineItem) => lineItem?.node?.merchandise?.product?.title !== 'Hawaii G.E.T')
              .map(lineItem => lineItem?.node?.id)
              .filter(Boolean),
          }).then(() => {
            cartLinesAdd({
              cartId: cart.id,
              lines: lineItemsFromCartJs,
            })
          })
        } else {
          console.log("Add only")
          cartLinesAdd({
            cartId: cart.id,
            lines: lineItemsFromCartJs,
          })
        }

        return
      }
      setTimeout(() => dispatch(setLineItemsUpdated(true)), 10)
    } else {
      // Redirect back to homepage if the cart is empty.
      window.location.href = "/"
    }
  }, [cartJsData, cart])

  useEffect(() => {
    console.log('cartLinesAddData', cartLinesAddData);
    if (!cartLinesAddData) return

    dispatch(
      setCart(cartLinesAddData.cartLinesAdd.cart),
    )
    setTimeout(() => dispatch(setLineItemsUpdated(true)), 10)
  }, [cartLinesAddData])

  useEffect(() => {
    if (!lineItemsUpdated || !cart.checkoutUrl || !cart.lines.edges?.length) return

    // Immediately redirect to Shopify if cart does not require shipping.
    if (cartJsData.requires_shipping === false) {
      window.location.replace(cart.checkoutUrl)
    }
  }, [lineItemsUpdated, cartJsData, cart])

  useEffect(() => {
    if (cartJsData?.items.length) {
      dispatch(
        setCheckoutProductVendors(cartJsData.items.map((item) => item.vendor)),
      )
    }
  }, [cartJsData])

  if (
    isCheckoutConfigLoading ||
    isGetCustomerLoading ||
    isGetCartLoading ||
    isCreateCartLoading ||
    isCartJsLoading ||
    isCartLinesRemoveLoading ||
    isCartLinesAddLoading ||
    !lineItemsUpdated ||
    (cart && cartJsData && !cartJsData?.requires_shipping)
  ) {
    return <FullPageLoader />
  }

  return (
    <Box>
      {downMd && (
        <Box padding={2}>
          <Typography component="h1" variant="h5">
            Kakaako Wine Cart
          </Typography>
        </Box>
      )}
      <Grid container direction={downMd ? "column-reverse" : "row"}>
        <Grid
          item
          lg={7}
          md={7}
          xs={12}
          display="flex"
          justifyContent="end"
          width="100%"
        >
          <Box
            padding={2}
            display="flex"
            flexDirection="column"
            width="100%"
            maxWidth="900px"
            minHeight={downMd ? "calc(100vh - 106px)" : "calc(100vh - 16px)"}
          >
            {!downMd && (
              <Typography component="h1" variant="h5">
                Kakaako Wine Cart
              </Typography>
            )}
            <Breadcrumbs />
            <Outlet />
            <FooterLinks />
          </Box>
        </Grid>
        <Grid item lg={5} md={5} xs={12} bgcolor="#F5F5F5">
          <Box
            display="flex"
            flexDirection="column"
            minHeight={downMd ? "auto" : "calc(100vh - 16px)"}
            sx={{
              borderLeft: "#eee  1px solid",
            }}
          >
            <Cart />
          </Box>
        </Grid>
      </Grid>
      <AutoApplyVolumeDiscountsModal />
      <OrderLimitsModal />
    </Box>
  )
}

export default CheckoutCart
